import { useState } from 'react';
import { Helmet } from "react-helmet-async";
import { Link } from 'react-router-dom';
import '../App.css';
import Header from '../components/Header';
import Footer from '../components/Footer';

function DementiaCare() {
  const [openSection, setOpenSection] = useState<string | null>(null);

  const toggleSection = (id: string) => {
    setOpenSection((current) => (current === id ? null : id));
  };

  return (
    <>
    <Helmet>
        <title>A Guide to the Different Stages of Dementia</title>
        <meta
          name="description"
          content="Learn about the most common types of dementia, including Alzheimer's disease, vascular dementia, and Lewy body dementia, and their symptoms."
        />
        <link rel="canonical" href="https://www.harpwood.house/dementia-care" />
      </Helmet>

    <div className="page">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <Header />

      <main id="main-content" className="care-page-tight">
        <section className="content-section dementia-care-page">
          <div className="dementia-care-container">
            <h1>Dementia Care at Harpwood House</h1>
          </div>
        </section>

        <section className="content-section dementia-care-intro">
          <div className="section__container">
            <div className="section__image">
              <img src="/harpwood_images/VEC609_016-scaled.jpg" alt="Dementia care at Harpwood House" />
            </div>
            <div className="section__text">
              <h2>Dementia Care in Harpwood House</h2>
              <p>Living with dementia presents unique challenges for individuals and their families. At Harpwood House, our specialist dementia care combines expert nursing support with purpose-designed environments that promote independence, wellbeing, and meaningful connection.</p>
              <p>Below are some of the most frequently asked questions about our dementia care to help you understand what we offer and how we can support you and your loved one.</p>
            </div>
          </div>
        </section>

        <section className="content-section dementia-accordion-section">
          <div className="section__container">
            <div className="section__text">
              <h2 className="dementia-faq-heading">Frequently Asked Questions:</h2>
              <div className="respite-faq-accordion">
                <div className="respite-faq-box">
                  <button type="button" className="respite-faq-box__header" onClick={() => toggleSection('what-is')} aria-expanded={openSection === 'what-is'} aria-controls="dementia-answer-what-is" id="dementia-question-what-is">
                    <span className="respite-faq-box__question">What is dementia care?</span>
                    <span className="respite-faq-box__arrow" aria-hidden="true">{openSection === 'what-is' ? '▲' : '▼'}</span>
                  </button>
                  <div id="dementia-answer-what-is" role="region" aria-labelledby="dementia-question-what-is" className="respite-faq-box__content" hidden={openSection !== 'what-is'}>
                    <p>Dementia affects everyone differently, requiring flexible, individualised support that adapts as needs change.</p>
                    <p><strong>Our dementia care provides:</strong></p>
                    <h3>Clinical expertise</h3>
                    <ul>
                      <li>24-hour nursing care from registered nurses trained in dementia care</li>
                      <li>Management of behaviours that challenge with understanding and patience</li>
                      <li>Support with physical health conditions alongside dementia</li>
                      <li>Medication management and monitoring</li>
                    </ul>
                    <h3>Person-centred approach</h3>
                    <ul>
                      <li>Care plans built around life history, preferences, and what brings joy</li>
                      <li>Support to maintain independence for as long as possible</li>
                      <li>Activities designed to stimulate memory and engagement</li>
                      <li>Familiar routines that provide comfort and security</li>
                    </ul>
                    <h3>Family support</h3>
                    <ul>
                      <li>Guidance and education about dementia progression</li>
                      <li>Open communication about care decisions</li>
                      <li>Involvement in care planning and daily life</li>
                      <li>Peace of mind through Care Records Online</li>
                    </ul>
                  </div>
                </div>

                <div className="respite-faq-box">
                  <button type="button" className="respite-faq-box__header" onClick={() => toggleSection('types')} aria-expanded={openSection === 'types'} aria-controls="dementia-answer-types" id="dementia-question-types">
                    <span className="respite-faq-box__question">What are the different types of dementia?</span>
                    <span className="respite-faq-box__arrow" aria-hidden="true">{openSection === 'types' ? '▲' : '▼'}</span>
                  </button>
                  <div id="dementia-answer-types" role="region" aria-labelledby="dementia-question-types" className="respite-faq-box__content" hidden={openSection !== 'types'}>
                    <p>Our teams have extensive experience caring for residents with all forms of dementia, including:</p>
                    <ul>
                      <li>Alzheimer's disease</li>
                      <li>Vascular dementia</li>
                      <li>Lewy body dementia</li>
                      <li>Frontotemporal dementia</li>
                      <li>Mixed dementia</li>
                      <li>Dementia with Parkinson's disease</li>
                      <li>Alcohol-related dementia</li>
                    </ul>
                    <p>We adapt our approach to each individual, recognising that dementia manifests differently in every person.</p>
                  </div>
                </div>

                <div className="respite-faq-box">
                  <button type="button" className="respite-faq-box__header" onClick={() => toggleSection('accommodate')} aria-expanded={openSection === 'accommodate'} aria-controls="dementia-answer-accommodate" id="dementia-question-accommodate">
                    <span className="respite-faq-box__question">How does Harpwood House accommodate residents with dementia?</span>
                    <span className="respite-faq-box__arrow" aria-hidden="true">{openSection === 'accommodate' ? '▲' : '▼'}</span>
                  </button>
                  <div id="dementia-answer-accommodate" role="region" aria-labelledby="dementia-question-accommodate" className="respite-faq-box__content" hidden={openSection !== 'accommodate'}>
                    <h3>Designed for wellbeing</h3>
                    <p>Our homes feature specialist dementia-friendly design that supports independence and reduces confusion:</p>
                    <ul>
                      <li>Clear signage and visual cues throughout</li>
                      <li>Colour contrasts to aid navigation</li>
                      <li>Safe outdoor spaces for walking and fresh air</li>
                      <li>Quiet areas for rest and calm</li>
                      <li>Activity spaces for engagement and stimulation</li>
                    </ul>
                  </div>
                </div>

                <div className="respite-faq-box">
                  <button type="button" className="respite-faq-box__header" onClick={() => toggleSection('daily-life')} aria-expanded={openSection === 'daily-life'} aria-controls="dementia-answer-daily-life" id="dementia-question-daily-life">
                    <span className="respite-faq-box__question">What does daily life look like for our residents with dementia?</span>
                    <span className="respite-faq-box__arrow" aria-hidden="true">{openSection === 'daily-life' ? '▲' : '▼'}</span>
                  </button>
                  <div id="dementia-answer-daily-life" role="region" aria-labelledby="dementia-question-daily-life" className="respite-faq-box__content" hidden={openSection !== 'daily-life'}>
                    <h3>Supporting daily routines</h3>
                    <p>We help residents maintain familiar routines and activities that provide structure and comfort:</p>
                    <ul>
                      <li>Personal care delivered with dignity and patience</li>
                      <li>Mealtimes that encourage social connection</li>
                      <li>Activities tailored to abilities and interests</li>
                      <li>Support with mobility and movement</li>
                      <li>Management of continence needs sensitively</li>
                    </ul>
                    <h3>Meaningful activities</h3>
                    <p>Our activity programmes focus on engagement and entertainment:</p>
                    <ul>
                      <li>Reminiscence therapy using music, photos, and memories</li>
                      <li>Gentle exercise and movement</li>
                      <li>Creative activities like art and crafts</li>
                      <li>Sensory stimulation</li>
                      <li>Social activities and communal gatherings</li>
                      <li>One-to-one time with staff</li>
                    </ul>
                  </div>
                </div>

                <div className="respite-faq-box">
                  <button type="button" className="respite-faq-box__header" onClick={() => toggleSection('technology')} aria-expanded={openSection === 'technology'} aria-controls="dementia-answer-technology" id="dementia-question-technology">
                    <span className="respite-faq-box__question">How do we use technology to support dementia care?</span>
                    <span className="respite-faq-box__arrow" aria-hidden="true">{openSection === 'technology' ? '▲' : '▼'}</span>
                  </button>
                  <div id="dementia-answer-technology" role="region" aria-labelledby="dementia-question-technology" className="respite-faq-box__content" hidden={openSection !== 'technology'}>
                    <p>We use innovative technology to enhance dementia care without intrusion:</p>
                    <ul>
                      <li>Sleep monitoring to ensure restful nights without unnecessary disturbance</li>
                      <li>Movement sensors that alert staff when support is needed</li>
                      <li>Call systems that provide reassurance</li>
                      <li>Real-time vital signs monitoring that helps us spot changes in wellbeing early</li>
                    </ul>
                  </div>
                </div>

                <div className="respite-faq-box">
                  <button type="button" className="respite-faq-box__header" onClick={() => toggleSection('challenging')} aria-expanded={openSection === 'challenging'} aria-controls="dementia-answer-challenging" id="dementia-question-challenging">
                    <span className="respite-faq-box__question">How do we support challenging behaviours that residents may experience?</span>
                    <span className="respite-faq-box__arrow" aria-hidden="true">{openSection === 'challenging' ? '▲' : '▼'}</span>
                  </button>
                  <div id="dementia-answer-challenging" role="region" aria-labelledby="dementia-question-challenging" className="respite-faq-box__content" hidden={openSection !== 'challenging'}>
                    <p>Behaviours that challenge often reflect unmet needs or distress. Our approach focuses on understanding and responding to the underlying cause:</p>
                    <p><strong>We focus on:</strong></p>
                    <ul>
                      <li>Identifying triggers and patterns</li>
                      <li>Creating calm, reassuring environments</li>
                      <li>Distraction and redirection techniques</li>
                      <li>Consistent, familiar staff who understand residents</li>
                      <li>Medication only when necessary and appropriate</li>
                    </ul>
                    <p>Our nurses and care staff receive specialist training in managing behaviours that challenge with empathy and professionalism.</p>
                  </div>
                </div>

                <div className="respite-faq-box">
                  <button type="button" className="respite-faq-box__header" onClick={() => toggleSection('stages')} aria-expanded={openSection === 'stages'} aria-controls="dementia-answer-stages" id="dementia-question-stages">
                    <span className="respite-faq-box__question">What are the different stages of dementia care?</span>
                    <span className="respite-faq-box__arrow" aria-hidden="true">{openSection === 'stages' ? '▲' : '▼'}</span>
                  </button>
                  <div id="dementia-answer-stages" role="region" aria-labelledby="dementia-question-stages" className="respite-faq-box__content" hidden={openSection !== 'stages'}>
                    <h3>Early-stage dementia</h3>
                    <p>In the early stages, we focus on maintaining independence and supporting remaining abilities. Residents often participate actively in social life and may require minimal support.</p>
                    <h3>Mid-stage dementia</h3>
                    <p>As dementia progresses, we increase support while maintaining dignity and choice. We help with daily tasks, manage confusion, and provide reassurance during difficult moments.</p>
                    <h3>Advanced dementia</h3>
                    <p>In later stages, we provide comprehensive care focused on comfort, dignity, and quality of life. Our care expertise ensures residents remain comfortable and pain-free.</p>
                    <p>Throughout every stage, we adapt our approach to meet changing needs while preserving individuality.</p>
                  </div>
                </div>

                <div className="respite-faq-box">
                  <button type="button" className="respite-faq-box__header" onClick={() => toggleSection('relationships')} aria-expanded={openSection === 'relationships'} aria-controls="dementia-answer-relationships" id="dementia-question-relationships">
                    <span className="respite-faq-box__question">Why are relationships so important for residents with dementia?</span>
                    <span className="respite-faq-box__arrow" aria-hidden="true">{openSection === 'relationships' ? '▲' : '▼'}</span>
                  </button>
                  <div id="dementia-answer-relationships" role="region" aria-labelledby="dementia-question-relationships" className="respite-faq-box__content" hidden={openSection !== 'relationships'}>
                    <h3>Maintaining connections</h3>
                    <p>We recognise that relationships are fundamental to wellbeing. Our spacious care suites provide comfortable spaces for family visits, and we actively encourage families to remain involved in daily life.</p>
                    <h3>Communication support</h3>
                    <p>As dementia affects communication, we:</p>
                    <ul>
                      <li>Train staff in effective communication techniques</li>
                      <li>Use visual aids and non-verbal cues</li>
                      <li>Allow time for responses without pressure</li>
                      <li>Focus on emotional connection over words</li>
                      <li>Help families understand changing communication needs</li>
                    </ul>
                  </div>
                </div>

                <div className="respite-faq-box">
                  <button type="button" className="respite-faq-box__header" onClick={() => toggleSection('staff')} aria-expanded={openSection === 'staff'} aria-controls="dementia-answer-staff" id="dementia-question-staff">
                    <span className="respite-faq-box__question">How are our staff trained to support residents with dementia?</span>
                    <span className="respite-faq-box__arrow" aria-hidden="true">{openSection === 'staff' ? '▲' : '▼'}</span>
                  </button>
                  <div id="dementia-answer-staff" role="region" aria-labelledby="dementia-question-staff" className="respite-faq-box__content" hidden={openSection !== 'staff'}>
                    <h3>Trained dementia specialists</h3>
                    <p>Our team here at Harpwood House receive ongoing dementia training covering:</p>
                    <ul>
                      <li>Understanding different types of dementia</li>
                      <li>Person-centred care approaches</li>
                      <li>Communication techniques</li>
                      <li>Managing behaviours that challenge</li>
                      <li>End-of-life care for dementia</li>
                    </ul>
                    <h3>Consistency and familiarity</h3>
                    <p>We assign small groups of residents to dedicated care teams. This consistency helps residents feel secure with familiar faces who understand their needs, preferences, and personalities.</p>
                  </div>
                </div>

                <div className="respite-faq-box">
                  <button type="button" className="respite-faq-box__header" onClick={() => toggleSection('when-consider')} aria-expanded={openSection === 'when-consider'} aria-controls="dementia-answer-when-consider" id="dementia-question-when-consider">
                    <span className="respite-faq-box__question">When might it be time to consider dementia care?</span>
                    <span className="respite-faq-box__arrow" aria-hidden="true">{openSection === 'when-consider' ? '▲' : '▼'}</span>
                  </button>
                  <div id="dementia-answer-when-consider" role="region" aria-labelledby="dementia-question-when-consider" className="respite-faq-box__content" hidden={openSection !== 'when-consider'}>
                    <p>It can be difficult to know when it's time to seek specialist dementia care. You might consider it when:</p>
                    <ul>
                      <li>Home care becomes insufficient for safety or wellbeing</li>
                      <li>Family carers are becoming exhausted or overwhelmed</li>
                      <li>Behaviours become challenging to manage at home</li>
                      <li>The person with dementia is at risk of wandering</li>
                      <li>Complex health needs develop alongside dementia</li>
                      <li>Isolation increases due to reduced social opportunities</li>
                      <li>Quality of life is diminishing despite home support</li>
                    </ul>
                    <p>There's no "right" time, but seeking support earlier often leads to better outcomes and reduces crisis situations.</p>
                  </div>
                </div>

                <div className="respite-faq-box">
                  <button type="button" className="respite-faq-box__header" onClick={() => toggleSection('supporting-families')} aria-expanded={openSection === 'supporting-families'} aria-controls="dementia-answer-supporting-families" id="dementia-question-supporting-families">
                    <span className="respite-faq-box__question">How do we support families in Surrey?</span>
                    <span className="respite-faq-box__arrow" aria-hidden="true">{openSection === 'supporting-families' ? '▲' : '▼'}</span>
                  </button>
                  <div id="dementia-answer-supporting-families" role="region" aria-labelledby="dementia-question-supporting-families" className="respite-faq-box__content" hidden={openSection !== 'supporting-families'}>
                    <h3>Your wellbeing matters too</h3>
                    <p>Caring for someone with dementia is emotionally and physically demanding. We support families by:</p>
                    <ul>
                      <li>Providing respite care to allow breaks</li>
                      <li>Offering guidance on dementia progression</li>
                      <li>Being available to answer questions and concerns</li>
                      <li>Including families in care decisions</li>
                      <li>Providing emotional support during difficult times</li>
                    </ul>
                    <h3>Staying connected</h3>
                    <p>Through our Care Records Online platform, you can access your loved one's care records in real time, even when you can't visit. This transparency provides reassurance and keeps you connected to their daily life.</p>
                  </div>
                </div>

                <div className="respite-faq-box">
                  <button type="button" className="respite-faq-box__header" onClick={() => toggleSection('funding')} aria-expanded={openSection === 'funding'} aria-controls="dementia-answer-funding" id="dementia-question-funding">
                    <span className="respite-faq-box__question">How is dementia care funded?</span>
                    <span className="respite-faq-box__arrow" aria-hidden="true">{openSection === 'funding' ? '▲' : '▼'}</span>
                  </button>
                  <div id="dementia-answer-funding" role="region" aria-labelledby="dementia-question-funding" className="respite-faq-box__content" hidden={openSection !== 'funding'}>
                    <h3>NHS Continuing Healthcare</h3>
                    <p>People with dementia may be eligible for NHS Continuing Healthcare, which fully funds care. We can guide you through the assessment process and support applications.</p>
                    <h3>Other funding options</h3>
                    <p>We also work with:</p>
                    <ul>
                      <li>Self-funding residents</li>
                      <li>Local authority funding</li>
                      <li>NHS-funded nursing care contributions</li>
                      <li>Joint funding arrangements</li>
                    </ul>
                    <p>Our team helps you understand your options and arrange appropriate funding for your circumstances.</p>
                  </div>
                </div>

                <div className="respite-faq-box">
                  <button type="button" className="respite-faq-box__header" onClick={() => toggleSection('first-step')} aria-expanded={openSection === 'first-step'} aria-controls="dementia-answer-first-step" id="dementia-question-first-step">
                    <span className="respite-faq-box__question">How do I take the first step towards dementia care?</span>
                    <span className="respite-faq-box__arrow" aria-hidden="true">{openSection === 'first-step' ? '▲' : '▼'}</span>
                  </button>
                  <div id="dementia-answer-first-step" role="region" aria-labelledby="dementia-question-first-step" className="respite-faq-box__content" hidden={openSection !== 'first-step'}>
                    <h3>A gradual transition</h3>
                    <p>We understand that moving into care is significant. We work at your pace, offering:</p>
                    <ul>
                      <li>Pre-admission visits to meet staff and see the environment</li>
                      <li>Trial stays to ease the transition</li>
                      <li>Familiar items from home to create comfort</li>
                      <li>Flexible moving-in schedules</li>
                      <li>Support for both residents and families during adjustment</li>
                    </ul>
                    <h3>Assessment and planning</h3>
                    <p>Before admission, we conduct a thorough assessment to ensure:</p>
                    <ul>
                      <li>We can meet all care needs appropriately</li>
                      <li>Our environment suits the individual</li>
                      <li>We understand preferences, history, and personality</li>
                      <li>Families feel confident in our approach</li>
                    </ul>
                  </div>
                </div>

                <div className="respite-faq-box">
                  <button type="button" className="respite-faq-box__header" onClick={() => toggleSection('whitstable')} aria-expanded={openSection === 'whitstable'} aria-controls="dementia-answer-whitstable" id="dementia-question-whitstable">
                    <span className="respite-faq-box__question">What does dementia care look like in Harpwood House?</span>
                    <span className="respite-faq-box__arrow" aria-hidden="true">{openSection === 'whitstable' ? '▲' : '▼'}</span>
                  </button>
                  <div id="dementia-answer-whitstable" role="region" aria-labelledby="dementia-question-whitstable" className="respite-faq-box__content" hidden={openSection !== 'whitstable'}>
                    <p>Here at our purpose built care home in Addlestone we offer specialist dementia care with dedicated areas and trained teams, offering:</p>
                    <ul>
                      <li>Purpose-built environment with dementia-friendly design</li>
                      <li>24-hour nursing care</li>
                      <li>Specialist activities and support</li>
                      <li>Secure outdoor spaces</li>
                      <li>Comfortable care suites for living well</li>
                    </ul>
                  </div>
                </div>

                <div className="respite-faq-box">
                  <button type="button" className="respite-faq-box__header" onClick={() => toggleSection('why-choose')} aria-expanded={openSection === 'why-choose'} aria-controls="dementia-answer-why-choose" id="dementia-question-why-choose">
                    <span className="respite-faq-box__question">Why do families choose our dementia care?</span>
                    <span className="respite-faq-box__arrow" aria-hidden="true">{openSection === 'why-choose' ? '▲' : '▼'}</span>
                  </button>
                  <div id="dementia-answer-why-choose" role="region" aria-labelledby="dementia-question-why-choose" className="respite-faq-box__content" hidden={openSection !== 'why-choose'}>
                    <p>Here at Harpwood House we specialise in caring for people with complex needs, including dementia. Families choose us because:</p>
                    <ul>
                      <li>Our clinical expertise ensures proper health management</li>
                      <li>Our person-centred approach maintains dignity and identity</li>
                      <li>Our purpose-built homes support independence safely</li>
                      <li>Our technology enhances care without intrusion</li>
                      <li>Our consistent staff build genuine relationships</li>
                      <li>Our family involvement keeps loved ones connected</li>
                    </ul>
                    <p>We combine the highest standards of nursing care with the warmth, understanding, and patience that dementia care requires.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="content-section get-in-touch-dementia">
          <div className="section__container">
            <div className="section__text get-in-touch-content">
              <h2 className="get-in-touch-heading">Get in touch</h2>
              <p>If you're considering dementia care for yourself or a loved one, the Harpwood House team is here to answer your questions and guide you through the process.</p>
              <p>Email us: <a href="mailto:enquiries@harpwood.house" className="nurse-care-link">enquiries@harpwood.house</a></p>
              <p>Call us: <a href="tel:01227533522" className="nurse-care-link">01227 533 522</a></p>
              <p>Or complete our <Link to="/contact-us" className="nurse-care-link">enquiry form</Link> and we'll be in touch within 24 hours to discuss how we can support you.</p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
    </>
  );
}

export default DementiaCare;
